import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { COMPONENT_QUERY, COMPONENT_QUERY_COMPARE } from '../../../constants/api';
import { TIME_PERIOD, WEEK_SUBSTRACTION_VALUE } from '../../../constants/datePickerConstants';
import api from '../../../services/api';
import {
  CHANNEL_OPPS,
  CHANNEL_OPPS_WITHOUT_LOCAL,
  OPPS_SPEND_CHANNEL_VENDOR_SEM,
  YOY_CHANNEL_PERFORMANCE_FILTER,
  YOY_OPPS_SPEND_BY_CHANNEL,
} from '../constants';
import {
  addTotalObject,
  sortByChannelAndVendor,
  transformYOYChannelResponseData,
  updateFilterValues,
} from '../services/ChannelOppsServices';
import { channelOppsActions } from '../../../modules/reducer/channelOppsSlice';
import { subBranchDashOppsByChannelActions } from '../../../modules/reducer/subBranchDashOppsByChannelSlice';
import { TransformDate } from '../../../components/DatePicker/DatePickerUtils/datePickerUtils';
import { previousWeekExtractor } from '../../../data/branchPerformanceData';
import selectors from '../../../modules/selectors/datePickerSelectors';
import {
  currentYearAndQuarterFun,
  getFilteredStoreOptions,
  isStoreValid,
} from '../../../constants/utils';

export const useTableDataHook = ({ subBranchDashboard }) => {
  const { userStores, userData } = useSelector((state) => state.auth);
  const location = useLocation();
  const options = useMemo(
    () =>
      getFilteredStoreOptions(userStores, userData?.data, subBranchDashboard, location.pathname),
    [userStores, userData, subBranchDashboard, location.pathname]
  );

  const { channelOppsRange } = useSelector((state) => state.channelOpps);
  const { subDashChannelOppsRange } = useSelector((state) => state.subBranchDashOppsByChannel);
  const { transformedDataWithDate } = useSelector(selectors.datePickerCombinedData);
  const [defaultFilter, setDefaultFilter] = useState(TIME_PERIOD);
  const [timePeriodFilter, setTimePeriodFilter] = useState([]);
  const dispatch = useDispatch();

  // Initialize storeId based on whether it's a sub-branch dashboard
  const [storeId, setStoreId] = useState(null);
  const [debouncedStoreId, setDebouncedStoreId] = useState(null); // For debouncing in multi-select mode
  const debounceTimerRef = useRef(null);
  const [resetStore, setResetStore] = useState(false);
  const [channelOppsRowdata, setChannelOppsRowdata] = useState([]);
  const [semOppsData, setSemOppsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [semOppsDataError, setSemOppsDataError] = useState(null);
  const [spendToggleState, setSpendToggleState] = useState(false);
  const [showSpendToggle, setShowSpendToggle] = useState(false);
  const [yoyChannelRowData, setYoyChannelRowData] = useState([]);
  const [yoyChannelLoading, setYoyChannelLoading] = useState(false);
  const [yoyChannelError, setYoyChannelError] = useState(null);
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [semOppsDataLoading, setSemOppsDataLoading] = useState(true);
  const [isSemOppsReportDownloading, setIsSemOppsReportDownloading] = useState(false);
  const initialLoading = useRef(true);

  // Initialize storeId when options are loaded
  useEffect(() => {
    if (options?.length > 0) {
      const initialValue = subBranchDashboard ? options.map((opt) => opt.value) : options[0]?.value;

      setStoreId(initialValue);
      setDebouncedStoreId(initialValue); // Initialize debounced value too
    }
  }, [options, subBranchDashboard]);

  const checkForGlobalObject = (object) => {
    if (object === null || object === undefined) {
      return false; // Return false if object is null or undefined
    }
    return (
      Object.values(object).every((value) => value === null || value === undefined) ||
      Object.values(object).some((value) => value === null || value === undefined)
    );
  };

  useEffect(() => {
    timePeriodRangeHandler();
    quarterAndYearValueHandler();
  }, [transformedDataWithDate, channelOppsRange, subDashChannelOppsRange]);

  // Updated to handle both single store ID and array of store IDs with debouncing
  const handleStoreIdChange = useCallback(
    (store) => {
      if (subBranchDashboard) {
        // For sub-branch dashboard, use debouncing
        const newValue = Array.isArray(store) ? store : [store];

        // Update the UI immediately
        setStoreId(newValue);

        // Clear any existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set a new timer for the debounced update
        debounceTimerRef.current = setTimeout(() => {
          setDebouncedStoreId(newValue);
        }, 500); // 500ms debounce delay
      } else {
        // For regular dashboard, update immediately without debouncing
        setStoreId(store);
        setDebouncedStoreId(store);
      }
    },
    [subBranchDashboard]
  );

  // Clean up timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  const handleResetForStore = () => {
    setResetStore(true);
  };

  const timePeriodRangeHandler = () => {
    const subBranchDashBoardRangeValues = subBranchDashboard
      ? subDashChannelOppsRange
      : channelOppsRange;
    const allValuesAreNull = checkForGlobalObject(subBranchDashBoardRangeValues);
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);

    if (allValuesAreNull && obj && Object.keys(obj).length && transformedDataWithDate?.length > 0) {
      const quarterValue =
        transformedDataWithDate?.length &&
        TransformDate?.quickFilters?.TWELVE_WEEK_DATA(
          transformedDataWithDate,
          previousWeekExtractor,
          WEEK_SUBSTRACTION_VALUE
        );

      !subBranchDashboard && dispatch(channelOppsActions.setChannelOppsRange(quarterValue));
      subBranchDashboard &&
        dispatch(subBranchDashOppsByChannelActions.setSubDashChannelOppsRange(quarterValue));
      const value = `w${subBranchDashBoardRangeValues?.start_week}, ${subBranchDashBoardRangeValues?.start_year} - w${subBranchDashBoardRangeValues?.end_week}, ${subBranchDashBoardRangeValues?.end_year}`;

      setDefaultFilter(value);
    }
  };

  const quarterAndYearValueHandler = () => {
    if (transformedDataWithDate?.length > 0) {
      const currentYearAndQuarter = currentYearAndQuarterFun(
        transformedDataWithDate,
        previousWeekExtractor,
        WEEK_SUBSTRACTION_VALUE,
        TransformDate
      );

      !subBranchDashboard &&
        dispatch(channelOppsActions.setCurrentQuarter(currentYearAndQuarter?.currentQuarterValue));
      !subBranchDashboard &&
        dispatch(channelOppsActions.setCurrentYear(currentYearAndQuarter?.currentYearBasedOnWeek));
      subBranchDashboard &&
        dispatch(
          subBranchDashOppsByChannelActions.setCurrentQuarter(
            currentYearAndQuarter?.currentQuarterValue
          )
        );
      subBranchDashboard &&
        dispatch(
          subBranchDashOppsByChannelActions.setCurrentYear(
            currentYearAndQuarter?.currentYearBasedOnWeek
          )
        );
    }
  };

  const getPayload = (key, timePeriodFilter) => {
    const payload = {
      [key]: {
        filters: timePeriodFilter,
      },
    };

    return payload;
  };

  /**
   * @param payload
   * @param key
   */
  async function fetchOppsByChannel(payload, key) {
    const response = await api.post(`${COMPONENT_QUERY}?component=${key}`, payload);

    return response.data;
  }

  const fetchData = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const key = spendToggleState ? CHANNEL_OPPS_WITHOUT_LOCAL : CHANNEL_OPPS;
      const payload = getPayload(key, timePeriodFilter);
      const result = await fetchOppsByChannel(payload, key);

      setChannelOppsRowdata(
        result?.data?.[key]?.result?.length > 0 ? addTotalObject(result?.data?.[key]?.result) : []
      );
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSemOppsData = async () => {
    setSemOppsDataLoading(true);
    setSemOppsDataError(null);
    setSemOppsData([]);
    try {
      const keyVendorsSEM = OPPS_SPEND_CHANNEL_VENDOR_SEM;
      const payload = getPayload(keyVendorsSEM, timePeriodFilter);
      const result = await fetchOppsByChannel(payload, keyVendorsSEM);

      setSemOppsData(
        result?.data?.[keyVendorsSEM]?.result?.length > 0
          ? addTotalObject(sortByChannelAndVendor(result?.data?.[keyVendorsSEM]?.result))
          : []
      );
    } catch (err) {
      setSemOppsDataError(true);
    } finally {
      setSemOppsDataLoading(false);
    }
  };

  // Update filter to handle multiple store IDs - Use debouncedStoreId instead of storeId
  useEffect(() => {
    if (!debouncedStoreId) {
      return;
    }

    setShowSpendToggle(isStoreValid(debouncedStoreId, options, subBranchDashboard));
    if (subBranchDashboard && !isStoreValid(debouncedStoreId, options, subBranchDashboard)) {
      setSpendToggleState(false);
    }
    const selectedRangeValues = subBranchDashboard ? subDashChannelOppsRange : channelOppsRange;

    if (!checkForGlobalObject(selectedRangeValues)) {
      setTimePeriodFilter([
        {
          col: 'dateindex',
          op: '>=',
          val: selectedRangeValues?.start_year
            ?.toString()
            ?.concat(
              selectedRangeValues?.start_week <= 9 && selectedRangeValues?.start_week > 0
                ? '0'.concat(selectedRangeValues?.start_week)
                : selectedRangeValues?.start_week
            ),
        },
        {
          col: 'dateindex',
          op: '<=',
          val: selectedRangeValues?.end_year
            ?.toString()
            ?.concat(
              selectedRangeValues?.end_week <= 9 && selectedRangeValues?.end_week > 0
                ? '0'.concat(selectedRangeValues?.end_week)
                : selectedRangeValues?.end_week
            ),
        },
        {
          col: 'store_id',
          op: 'IN',
          val: Array.isArray(debouncedStoreId) ? debouncedStoreId : [debouncedStoreId],
        },
      ]);
    }
  }, [debouncedStoreId, channelOppsRange, subDashChannelOppsRange]);

  useEffect(() => {
    setIsLoading(true);
    if (timePeriodFilter?.length > 0) {
      fetchData();
    }
  }, [timePeriodFilter, spendToggleState]);

  useEffect(() => {
    setSemOppsDataLoading(true);
    if (timePeriodFilter?.length > 0) {
      fetchSemOppsData();
    }
  }, [timePeriodFilter]);

  // Update YoY fetch to handle multiple stores - use debouncedStoreId
  const fetchYoYChannelPerformance = async (key, currentWeekData) => {
    try {
      setYoyChannelLoading(true);
      initialLoading.current = false;
      setYoyChannelError(null);

      // If in sub-branch dashboard with multiple stores selected, use the first one for YoY data
      const storeIdForYoY =
        Array.isArray(debouncedStoreId) && debouncedStoreId.length > 0
          ? debouncedStoreId[0]
          : debouncedStoreId;

      const payload = {
        ...YOY_CHANNEL_PERFORMANCE_FILTER,
        current_query_builder: {
          ...YOY_CHANNEL_PERFORMANCE_FILTER.current_query_builder,
          filters: updateFilterValues(
            YOY_CHANNEL_PERFORMANCE_FILTER.current_query_builder.filters,
            currentWeekData.currentYear,
            currentWeekData.currentWeek,
            storeIdForYoY
          ),
        },
        previous_query_builder: {
          ...YOY_CHANNEL_PERFORMANCE_FILTER.previous_query_builder,
          filters: updateFilterValues(
            YOY_CHANNEL_PERFORMANCE_FILTER.previous_query_builder.filters,
            currentWeekData.currentYear - 1,
            currentWeekData.currentWeek,
            storeIdForYoY
          ),
        },
      };

      const response = await api.post(`${COMPONENT_QUERY_COMPARE}?component=${key}`, payload);
      const transformedData = transformYOYChannelResponseData(response.data.data);

      setYoyChannelRowData(transformedData);
    } catch (error) {
      setYoyChannelError(true);
    } finally {
      setYoyChannelLoading(false);
    }
  };

  // Update to check for valid debouncedStoreId value
  useEffect(() => {
    if (!debouncedStoreId) {
      return;
    }

    // Skip YoY data fetching entirely when in subBranchDashboard mode
    if (subBranchDashboard) {
      return;
    }
    const currentWeekData = previousWeekExtractor(transformedDataWithDate);
    const hasValidStoreId = Array.isArray(debouncedStoreId)
      ? debouncedStoreId.length > 0
      : Boolean(debouncedStoreId);

    if (hasValidStoreId && currentWeekData.currentYear && !yoyChannelLoading) {
      fetchYoYChannelPerformance(YOY_OPPS_SPEND_BY_CHANNEL, currentWeekData);
    }
  }, [debouncedStoreId, transformedDataWithDate, subBranchDashboard]);

  return {
    options,
    defaultFilter,
    resetStore,
    handleResetForStore,
    handleStoreIdChange,
    channelOppsRowdata,
    isLoading,
    error,
    spendToggleState,
    setSpendToggleState,
    storeId, // Return the immediate storeId for UI purposes
    showSpendToggle,
    userData,
    yoyChannelRowData,
    yoyChannelLoading,
    yoyChannelError,
    initialLoading,
    setIsReportDownloading,
    isReportDownloading,
    timePeriodFilter,
    semOppsData,
    semOppsDataLoading,
    semOppsDataError,
    isSemOppsReportDownloading,
    setIsSemOppsReportDownloading,
  };
};
